import _ from 'lodash';
import type { z } from 'zod';
import { Subject } from '../common/enums';
import { SearchStringSchema } from '../common/schemas';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { StaffAttributes } from './constants';
import { DBStaffSchema, StaffSchema } from './schemas';

type Staff = z.infer<typeof StaffSchema>;

export const StaffCodecConfig: TransformerConfig<typeof StaffSchema, typeof DBStaffSchema> = {
  name: 'Staff',
  clientSchema: StaffSchema,
  dbSchema: DBStaffSchema,
  transformers: [
    // classes: [{ subject, classId }] ↔ classes: { "subject#classId": {} }
    createTransformer<typeof StaffSchema, typeof DBStaffSchema>({
      encoder: {
        sourceFields: ['classes'] as const,
        encode: ({ classes }) => ({
          classes: Object.fromEntries(
            classes.map((c: Staff['classes'][number]) => [`${c.subject}#${c.classId}`, {}])
          ),
        }),
      },
      decoder: {
        sourceFields: ['classes'] as const,
        decode: ({ classes }) => ({
          classes: Object.keys(classes ?? {}).map(key => {
            const [subject, classId] = key.split('#');
            return { subject: subject as Subject, classId: classId! };
          }),
        }),
      },
    }),

    // Handle search string updates
    createTransformer<typeof StaffSchema, typeof DBStaffSchema>({
      encoder: {
        sourceFields: StaffAttributes.Search,
        dependencies: StaffAttributes.Search,
        encode: staff => {
          let searchStr = '';
          StaffAttributes.Search.forEach(attr => {
            const val = staff[attr];
            searchStr += SearchStringSchema.parse(val);
          });
          return { searchStr, ..._.pick(staff, StaffAttributes.Search) };
        },
      },
    }),
  ],
};

export const encodeStaffToDb = createEncodeToDbFn(StaffCodecConfig);
export const decodeStaffFromDb = createDecodeFromDbFn(StaffCodecConfig);
